import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError, type User, type ProgressSummary, type Activity, type Companion } from "./lib/api";
import { MILESTONES, TOTAL_MILES, QUEST_MILES, type Milestone } from "../shared/route/index.ts";
import { walkerFromId } from "../shared/avatars.ts";
import { loadPrefs, savePrefs, applyTheme, resolveTheme, onSystemThemeChange, systemReducedMotion, type ThemePref } from "./lib/prefs";
import { fmt, conv, fmtMiles, type Units } from "./lib/format";
import IconDefs from "./components/Icons";
import JourneyMap, { type JourneyMapHandle } from "./components/JourneyMap";
import BottomNav, { type Screen } from "./components/BottomNav";
import SignIn from "./components/SignIn";
import LogSheet, { type NewActivity } from "./components/LogSheet";
import Celebration, { type CelebrationState } from "./components/Celebration";
import JourneyScreen from "./components/JourneyScreen";
import FellowshipScreen from "./components/FellowshipScreen";
import WalkerScreen from "./components/WalkerScreen";

type Session =
  | { status: "loading" }
  | { status: "anonymous" }
  | { status: "ready"; user: User; progress: ProgressSummary; activities: Activity[] };

const majorsBetween = (a: number, b: number) => MILESTONES.filter((m) => m.major && m.mile > 0 && m.mile > a && m.mile <= b);

export default function App() {
  const [session, setSession] = useState<Session>({ status: "loading" });
  const [screen, setScreen] = useState<Screen>("map");
  const [logOpen, setLogOpen] = useState(false);
  const [cel, setCel] = useState<CelebrationState | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [displayMile, setDisplayMile] = useState(0);
  const [narrow, setNarrow] = useState(() => window.innerWidth < 700);
  const [prefs, setPrefs] = useState(loadPrefs);
  const [companions, setCompanions] = useState<Companion[]>([]);
  const loadCompanions = useCallback(() => api.companions().then((r) => setCompanions(r.companions)).catch(() => setCompanions([])), []);
  const mapRef = useRef<JourneyMapHandle>(null);
  const rafRef = useRef(0);
  const toastTimer = useRef(0);

  const reducedMotion = prefs.reducedMotion || systemReducedMotion();

  useEffect(() => {
    applyTheme(resolveTheme(prefs.theme));
    document.documentElement.classList.toggle("reduce-motion", prefs.reducedMotion);
    savePrefs(prefs);
    // Follow the device while the preference is "system".
    return prefs.theme === "system" ? onSystemThemeChange(() => applyTheme(resolveTheme("system"))) : undefined;
  }, [prefs]);

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 700);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const load = useCallback(async () => {
    try {
      const { user, progress } = await api.me();
      const { activities } = await api.activities();
      setSession({ status: "ready", user, progress, activities });
      setDisplayMile(progress.totalMiles);
      void loadCompanions();
      // Celebrate any major milestone reached but never shown on this device.
      const unseen = MILESTONES.filter((m) => m.major && m.mile > 0 && progress.unseenMilestones.includes(m.mile));
      if (unseen.length) setCel({ list: unseen, idx: 0 });
      else if (progress.unseenMilestones.length) void api.markMilestonesSeen(progress.unseenMilestones);
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 401)) console.error(e);
      setSession({ status: "anonymous" });
      setDisplayMile(0);
    }
  }, [loadCompanions]);

  useEffect(() => { void load(); }, [load]);

  const patch = (p: Partial<Extract<Session, { status: "ready" }>>) =>
    setSession((s) => (s.status === "ready" ? { ...s, ...p } : s));

  const showToast = (msg: string) => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 2800);
  };

  /** Animate the walker from mile a to b with the camera following, then celebrate or toast. */
  const animateTo = (a: number, b: number, crossed: Milestone[], units: Units) => {
    const done = () => {
      setDisplayMile(b);
      mapRef.current?.centerOn(b);
      if (crossed.length) setCel({ list: crossed, idx: 0 });
      else showToast(`+${fmt(conv(b - a, units))} ${units} — the Road goes ever on`);
    };
    cancelAnimationFrame(rafRef.current);
    if (reducedMotion || b <= a) return done();
    const dur = Math.min(2400, 900 + (b - a) * 8);
    const t0 = performance.now();
    const step = (t: number) => {
      const k = Math.min(1, (t - t0) / dur);
      const e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
      const m = a + (b - a) * e;
      setDisplayMile(m);
      mapRef.current?.centerOn(m);
      if (k < 1) rafRef.current = requestAnimationFrame(step);
      else done();
    };
    rafRef.current = requestAnimationFrame(step);
  };

  const addActivity = async (a: NewActivity) => {
    if (session.status !== "ready") return;
    const before = session.progress.totalMiles;
    const r = await api.addActivity(a);
    patch({ activities: [r.activity, ...session.activities], progress: r.progress });
    setLogOpen(false);
    setScreen("map");
    animateTo(before, r.progress.totalMiles, majorsBetween(before, r.progress.totalMiles), session.user.units);
  };

  const deleteActivity = async (id: string) => {
    if (session.status !== "ready" || !confirm("Remove this outing from your journey?")) return;
    const { progress } = await api.deleteActivity(id);
    patch({ activities: session.activities.filter((x) => x.id !== id), progress });
    setDisplayMile(progress.totalMiles);
    mapRef.current?.centerOn(progress.totalMiles);
  };

  const onward = async () => {
    if (!cel) return;
    if (cel.idx < cel.list.length - 1) return setCel({ ...cel, idx: cel.idx + 1 });
    setCel(null);
    if (session.status === "ready" && session.progress.unseenMilestones.length) {
      await api.markMilestonesSeen(session.progress.unseenMilestones);
      patch({ progress: { ...session.progress, unseenMilestones: [] } });
    }
  };

  const signOut = async () => {
    await api.logout();
    setSession({ status: "anonymous" });
    setScreen("map");
    setDisplayMile(0);
    setCompanions([]);
  };

  const ready = session.status === "ready" ? session : null;
  const walker = useMemo(() => walkerFromId(ready?.user.avatar_id), [ready?.user.avatar_id]);
  const units: Units = ready?.user.units ?? "mi";
  const progress = ready?.progress;
  const total = progress?.totalMiles ?? 0;
  const finished = total >= TOTAL_MILES;
  // The HUD counts down to the next story milestone (the ones with celebration cards), not every waypoint.
  const nextMajor = useMemo(() => MILESTONES.find((m) => m.major && m.mile > total), [total]);

  return (
    <div className="app">
      <IconDefs />
      <JourneyMap
        ref={mapRef}
        displayMile={displayMile}
        walker={walker}
        signedIn={Boolean(ready)}
        reducedMotion={reducedMotion}
        showMiniMap={Boolean(ready) && narrow && screen === "map"}
        companions={ready ? companions : []}
      />

      {session.status === "loading" && <div className="centered" style={{ position: "absolute", inset: 0 }}>Unrolling the map…</div>}
      {session.status === "anonymous" && <SignIn onSignedIn={load} />}

      {ready && progress && (
        <>
          {screen === "map" && (
            <>
              <div className="hud-row">
                <div className="hud">
                  <div className="overline">{finished ? "Journey complete" : `Leg ${progress.segment.order} of 8`}</div>
                  <div className="hud-leg">{finished ? "There and back again" : progress.segment.name}</div>
                  <div className="hud-total"><b>{fmtMiles(total, units)}</b><span> of {fmtMiles(TOTAL_MILES, units)}</span></div>
                  <div className="hud-next">
                    {finished ? "Every mile is walked." : nextMajor ? `${nextMajor.mapLabel ?? nextMajor.title} in ${fmtMiles(nextMajor.mile - total, units)}` : ""}
                  </div>
                </div>
              </div>
              {total <= 0 && !logOpen && (
                <div className="banner">
                  <div className="banner-title">You are standing at the door of Bag End.</div>
                  <div className="banner-sub">{fmtMiles(QUEST_MILES, units)} to Mount Doom. The Road goes ever on and on — log your first outing to take the first step.</div>
                </div>
              )}
              {finished && !logOpen && !cel && (
                <div className="banner">
                  <div className="banner-title big">“Well, I’m back.”</div>
                  <div className="banner-sub">{fmtMiles(TOTAL_MILES, units)}, there and back again. The journey is complete.</div>
                </div>
              )}
              <div className="cta-wrap">
                <button className="btn-primary" onClick={() => setLogOpen(true)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4v16M4 12h16" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" fill="none" /></svg>
                  Log activity
                </button>
              </div>
            </>
          )}

          {screen === "journey" && (
            <JourneyScreen progress={progress} activities={ready.activities} units={units} onDelete={deleteActivity} />
          )}

          {screen === "fellowship" && <FellowshipScreen units={units} userId={ready.user.id} onChanged={loadCompanions} />}

          {screen === "walker" && (
            <WalkerScreen
              user={ready.user}
              walker={walker}
              theme={prefs.theme}
              reducedMotion={prefs.reducedMotion}
              onUser={(u) => patch({ user: u })}
              onTheme={(theme: ThemePref) => setPrefs((p) => ({ ...p, theme }))}
              onReducedMotion={(reducedMotion) => setPrefs((p) => ({ ...p, reducedMotion }))}
              onSignOut={signOut}
              onDeleted={() => { setSession({ status: "anonymous" }); setScreen("map"); setDisplayMile(0); }}
            />
          )}

          <BottomNav screen={screen} onChange={(s) => { setScreen(s); setLogOpen(false); }} />

          {logOpen && (
            <LogSheet units={units} onUnits={(u) => api.updateMe({ units: u }).then(({ user }) => patch({ user }))} onClose={() => setLogOpen(false)} onSubmit={addActivity} />
          )}
          {cel && <Celebration cel={cel} onward={onward} />}
          {toast && <div className="toast">{toast}</div>}
        </>
      )}
    </div>
  );
}
